'use client';
import { useTranslations } from 'next-intl';
import { type FC, type PropsWithChildren, useEffect, useId, useRef, useState } from 'react';
import { ButtonTab } from '@/app/components/shared/button-tab';
import { useStore } from '@/app/providers/store';
import type { ImageToolValues, VideoToolValues } from '@/app/types';
import { cn } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { Tab } from '../types';
import { TaskHistory } from './TaskHistory';
import { useTaskHistoryRouteContext } from './TaskHistory/use-task-history-route-context';

export const TabForm: FC<PropsWithChildren & { className?: string }> = ({ children, className }) => {
  const router = useRouter();
  const { updateLastActiveVideoTool } = useStore((state) => ({
    updateLastActiveVideoTool: state.updateLastActiveVideoTool,
  }));
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<VideoToolValues | ImageToolValues | null>(null);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const scrollableTargetId = useId();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { createPathname, defaultTaskTypeKeys, tutorialEmptyState, ttsOnlyHistory } = useTaskHistoryRouteContext();

  useEffect(() => {
    const videoTabs: Tab[] = [
      {
        value: 'character2video',
        label: t('create.tabCharacter2Video'),
      },
      {
        value: 'img2video',
        label: t('create.tabImg2Video'),
      },
      {
        value: 'text2video',
        label: t('create.tabText2Video'),
      },
    ];
    const imageTabs: Tab[] = [
      {
        value: 'reference2image',
        label: t('create.tabReference2Image'),
      },
      {
        value: 'text2image',
        label: t('create.tabText2Image'),
      },
    ];

    // 检查是否是视频工具路径
    const activeVideoTab = videoTabs.find((tab) => createPathname.includes(tab.value))?.value;
    if (
      activeVideoTab &&
      (activeVideoTab === 'character2video' || activeVideoTab === 'img2video' || activeVideoTab === 'text2video')
    ) {
      setActiveTab(activeVideoTab);
      updateLastActiveVideoTool(activeVideoTab as VideoToolValues);
      setTabs(videoTabs);
      return;
    }

    // 检查是否是图片工具路径
    const activeImageTab = imageTabs.find((tab) => createPathname.includes(tab.value))?.value;
    if (activeImageTab && (activeImageTab === 'reference2image' || activeImageTab === 'text2image')) {
      setActiveTab(activeImageTab);
      setTabs(imageTabs);
      return;
    }

    // 没有匹配的 tab
    setTabs([]);
    setActiveTab(null);

    // 当 tabs 变为空时，将滚动容器重置到顶部
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [t, createPathname, updateLastActiveVideoTool]);

  return (
    <div
      ref={scrollContainerRef}
      id={scrollableTargetId}
      className={cn(
        'mx-auto flex w-full gap-3 px-4 pb-6 sm:h-full sm:flex-col sm:items-center sm:gap-2 sm:overflow-y-auto sm:px-3 lg:pb-4',
        className,
      )}
    >
      <div
        className={cn(
          'relative w-[38%] max-w-[488px] sm:w-full sm:min-w-[334px] md:max-w-full lg:min-w-[384px]',
          ttsOnlyHistory && 'w-2/3 max-w-none lg:w-1/2 lg:min-w-0',
        )}
      >
        {tabs.length > 0 && activeTab && (
          <div className="absolute top-0 left-0 z-20 mb-3 h-11 w-full rounded-b-12 bg-system-bg01 sm:w-full">
            <ButtonTab
              className="flex size-full justify-between gap-0 bg-[#1A1C20] p-0"
              buttonClassName="flex-1 px-3 aria-selected:bg-[#313338] p-0 h-full rounded-12"
              active={activeTab}
              onChange={(val) => router.push(`/create/${val}`)}
              options={tabs}
            />
          </div>
        )}
        <div
          className={cn(
            'hide-scrollbar flex h-full w-full flex-col overflow-y-scroll pb-[72px] sm:h-auto sm:min-h-[420px]',
            tabs.length && 'pt-[56px]',
          )}
          id="tool-container"
        >
          {children}
        </div>
      </div>
      <TaskHistory
        scrollableTargetId={scrollableTargetId}
        defaultTaskTypeKeys={defaultTaskTypeKeys}
        tutorialEmptyState={tutorialEmptyState}
        ttsOnly={ttsOnlyHistory}
      />
    </div>
  );
};
